import {
    assign,
    createActor,
    emit,
    fromPromise,
    setup,
    SnapshotFrom,
} from 'xstate5';
import { api, refreshCSRFToken } from '$api';
import { AuthDetails, SSOConfig, Message } from '@/auth/modes/types';
import translations from '@/i18n/messages';

export const modesMachine = setup({
    types: {
        context: {} as {
            details: AuthDetails | null;
            error: string | null;
        },

        events: {} as
            | ({ type: 'authenticating' } & SSOConfig)
            | { type: 'authenticated' }
            | { type: 'errored' }
            | { type: 'invalidating' }
            | ({ type: 'logout' } & SSOConfig)
            | { type: 'refresh' }
            | { type: 'messaging' },

        emitted: {} as
            | { type: 'unauthenticated' }
            | { type: 'authenticated'; details: AuthDetails }
            | { type: 'refreshed'; details: AuthDetails }
            | { type: 'errored'; message: string },
    },

    actions: {
        emitUnauthenticatedEvent: emit({ type: 'unauthenticated' }),

        emitAuthenticatedEvent: emit(({ context }) => {
            if (!context.details) {
                throw new Error(
                    'Cannot emit authenticated event when not' +
                        ' authenticated. `context.details` cannot be null when' +
                        ' authenticated',
                );
            }

            return {
                type: 'authenticated',
                details: context.details,
            } as const;
        }),

        emitRefreshedEvent: emit(({ context }) => {
            if (!context.details) {
                throw new Error(
                    'Cannot emit refreshed event when not authenticated.' +
                    '`context.details` cannot be null when authenticated',
                );
            }

            return {
                type: 'refreshed',
                details: context.details,
            } as const;
        }),

        emitErroredEvent: emit(({ context }) => {
            if (!context.error) {
                throw new Error(
                    'Cannot emit errored event when not in `errored` state',
                );
            }

            return {
                type: 'errored',
                message: context.error,
            } as const;
        }),
    },

    actors: {
        authenticate: fromPromise<void, SSOConfig>(async ({ input }) => {
            window.location.href = input.apiBaseUrl + '/auth/sso/login';
        }),

        refresh: fromPromise(async () => {
            await refreshCSRFToken();
            const { data } = await api.get<AuthDetails>('/auth/session');
            return data;
        }),

        message: fromPromise<void, Message>(async ({ input }) => {
            throw new Error(input.message);
        }),

        logout: fromPromise<void, SSOConfig>(async ({ input }) => {
            window.location.href = input.apiBaseUrl + '/auth/sso/logout';
        }),
    },
}).createMachine({
    initial: 'unauthenticated',

    context: {
        details: null,
        error: null,
    },

    states: {
        unauthenticated: {
            entry: [{ type: 'emitUnauthenticatedEvent' }],
            on: {
                authenticating: 'authenticating',
                refresh: 'refreshing',
                messaging: 'message',
            },
        },

        authenticating: {
            on: {
                refresh: 'unauthenticated',
            },
            invoke: {
                id: 'authenticate',
                src: 'authenticate',
                input: ({ event }) => {
                    if (event.type === 'authenticating') {
                        return event;
                    }

                    throw new Error('Authenticating requires auth config.');
                },
                onError: {
                    target: 'errored',
                    actions: assign(({ event }) => {
                        return {
                            error: (event as any).error.message,
                        };
                    }),
                },
            },
        },

        authenticated: {
            entry: [{ type: 'emitAuthenticatedEvent' }],
            on: {
                invalidating: 'unauthenticated',
                logout: 'loggingOut',
            },
        },

        refreshing: {
            invoke: {
                id: 'refreshing',
                src: 'refresh',
                onDone: {
                    target: 'authenticated',
                    actions: [
                        assign(({ event }) => {
                            return {
                                details: {
                                    token: event.output.token,
                                    email: event.output.email,
                                    payload: event.output.payload,
                                },
                            };
                        }),
                        { type: 'emitRefreshedEvent' },
                    ]
                },
                onError: {
                    target: 'errored',
                    actions: [
                        assign({
                            error: 'You have been logged out.',
                        }),
                    ],
                },
            },
        },

        loggingOut: {
            entry: [{ type: 'emitUnauthenticatedEvent' }],
            invoke: {
                id: 'loggingOut',
                src: 'logout',
                input: ({ event }) => {
                    if (event.type === 'logout') {
                        return event;
                    }

                    throw new Error('Logging out requires auth config.');
                },
                onError: {
                    target: 'errored',
                    actions: assign(({ event }) => {
                        return {
                            error: (event as any).error.message,
                        };
                    }),
                },
            },
        },

        message: {
            invoke: {
                id: 'messaging',
                src: 'message',
                input: () => {
                    const params = new URLSearchParams(document.location.search);
                    let key = params.get('msg');

                    if (key === null || ! (key in translations['en'])) {
                        key = 'error.technical';
                    }

                    return {
                        message: translations['en'][key]
                    };
                },
                onError: {
                    target: 'errored',
                    actions: assign(({ event }) => {
                        return {
                            error: (event as any).error.message,
                        };
                    }),
                },
            }
        },

        errored: {
            entry: [{ type: 'emitErroredEvent' }],
            on: {
                authenticating: 'authenticating',
            },
        },
    },
});

let actor = createActor(modesMachine);

let started = false;

export function reset() {
    actor = createActor(modesMachine);
    actor.start();
}

export function start() {
    if (!started) {
        actor.start();

        const params = new URLSearchParams(document.location.search);

        params.has('msg')
            ? actor.send({ type: 'messaging' })
            : actor.send({ type: 'refresh' });

        started = true;
    }
}

export function logout(config?: SSOConfig) {
    if (!config) {
        throw new Error(
            'Config must be injected by this point. This ' +
            'is a serious programming error and the application is ' +
            'in an invalid state.',
        );
    }

    actor.send({ type: 'logout', ...config });
}

export function initiateSSO(config: SSOConfig) {
    actor.send({ type: 'authenticating', ...config });
}

export function authenticated(snapshot?: SnapshotFrom<typeof modesMachine>) {
    snapshot ??= actor.getSnapshot();
    return snapshot.matches('authenticated');
}

export { actor };
